// ChatbotPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { chatbotSendMessage } from "../services/chatbot.service";
import DashboardLayout from "../../../layouts/DashboardLayout";

export default function ChatbotPage() {
  const [messages, setMessages] = useState([
    // opcional: mensaje inicial
    // { role: "assistant", text: "Hola, ¿en qué puedo ayudarte?" },
  ]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll al final cuando cambian mensajes o loading
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading]);

  // Enfocar input al montar
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    const userMessage = input.trim();
    if (!userMessage || loading) return;

    setError("");

    // 1) agregar mensaje del usuario
    setMessages((prev) => [...prev, { role: "user", text: userMessage }]);
    setInput("");
    setLoading(true);

    try {
      // 2) llamar API
      const { data } = await chatbotSendMessage({
        prompt: userMessage,
        conversationId,
      });

      // Validación mínima
      const botText = typeof data?.text === "string" ? data.text : "";
      if (!botText) throw new Error("Respuesta inválida del chatbot");

      // 3) agregar respuesta del bot
      setMessages((prev) => [...prev, { role: "assistant", text: botText }]);

      // 4) actualizar conversationId si viene
      if (data?.conversationId) setConversationId(data.conversationId);
    } catch (err) {
      console.error("CHATBOT ERROR:", err);

  const status = err?.response?.status;
  const data = err?.response?.data;
  const msg = err?.message;

  console.error("status:", status);
  console.error("data:", data);
  console.error("msg:", msg);

  setError(
    status
      ? `Error al comunicarse con el chatbot (HTTP ${status})`
      : "Error al comunicarse con el chatbot"
  );
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSend();
  };

  const onKeyDown = (e) => {
    // Enter envía, Shift+Enter salto de línea
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const styles = {
    wrapper: {
      width: "100%",
      maxWidth: 760,
      margin: "0 auto",
      height: "72vh",
      display: "flex",
      flexDirection: "column",
      border: "1px solid #2b2b2b",
      borderRadius: 14,
      overflow: "hidden",
      background: "#0f1115",
      color: "#eaeaea",
    },
    header: {
      padding: "12px 14px",
      borderBottom: "1px solid #2b2b2b",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      fontWeight: 700,
    },
    sub: { fontSize: 12, opacity: 0.75, fontWeight: 400 },
    list: {
      flex: 1,
      overflowY: "auto",
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    row: (role) => ({
      display: "flex",
      justifyContent: role === "user" ? "flex-end" : "flex-start",
    }),
    bubble: (role) => ({
      maxWidth: "78%",
      padding: "10px 12px",
      borderRadius: 14,
      whiteSpace: "pre-wrap",
      lineHeight: 1.35,
      background: role === "user" ? "#1d4ed8" : "#1c1f26",
      border: role === "user" ? "1px solid #1e40af" : "1px solid #2b2b2b",
    }),
    typing: {
      fontSize: 13,
      opacity: 0.8,
      fontStyle: "italic",
      padding: "6px 2px 0 2px",
    },
    error: {
      margin: "10px 14px 0 14px",
      padding: "10px 12px",
      borderRadius: 12,
      border: "1px solid #7f1d1d",
      background: "#2a1212",
      color: "#fecaca",
      fontSize: 14,
    },
    footer: {
      borderTop: "1px solid #2b2b2b",
      padding: 12,
      display: "flex",
      gap: 10,
      alignItems: "flex-end",
      background: "#0f1115",
    },
    textarea: {
      flex: 1,
      minHeight: 42,
      maxHeight: 120,
      resize: "vertical",
      borderRadius: 12,
      border: "1px solid #2b2b2b",
      background: "#0b0d11",
      color: "#eaeaea",
      padding: "10px 12px",
      outline: "none",
    },
    btn: {
      minWidth: 96,
      height: 42,
      borderRadius: 12,
      border: "1px solid #2b2b2b",
      background: loading ? "#1c1f26" : "#16a34a",
      color: "#fff",
      cursor: loading ? "not-allowed" : "pointer",
      fontWeight: 700,
    },
  };

  return (
    <DashboardLayout>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <div>
            Chatbot{" "}
            <div style={styles.sub}>
              {conversationId ? `conversationId: ${conversationId}` : "Nueva conversación"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (loading) return;
              setMessages([]);
              setConversationId(null);
              setError("");
              inputRef.current?.focus();
            }}
            style={{
              borderRadius: 10,
              border: "1px solid #2b2b2b",
              background: "#1c1f26",
              color: "#eaeaea",
              padding: "8px 10px",
              cursor: loading ? "not-allowed" : "pointer",
            }}
            disabled={loading}
            title="Nueva conversación"
          >
            Nuevo
          </button>
        </div>
  
        {error ? <div style={styles.error}>{error}</div> : null}
  
        <div ref={listRef} style={styles.list}>
          {messages.map((m, idx) => (
            <div key={idx} style={styles.row(m.role)}>
              <div style={styles.bubble(m.role)}>{m.text}</div>
            </div>
          ))}
  
          {loading ? (
            <div style={styles.row("assistant")}>
              <div style={styles.bubble("assistant")}>
                <div style={styles.typing}>Escribiendo...</div>
              </div>
            </div>
          ) : null}
        </div>
  
        <form onSubmit={onSubmit} style={styles.footer}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Escribe tu mensaje... (Enter para enviar, Shift+Enter para salto de línea)"
            style={styles.textarea}
            disabled={loading}
          />
          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? "..." : "Enviar"}
          </button>
        </form>
      </div>
    </DashboardLayout>
  );
}